import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { NavigationButtons } from '@/components/NavigationButtons';
import { HeroSearch } from '@/components/HeroSearch';
import { PropertyCard } from '@/components/PropertyCard';
import SkeletonCard from '@/components/SkeletonCard';
import EmptyState from '@/components/EmptyState';
import LocationAutocomplete from '@/components/LocationAutocomplete';
import MapView from '@/components/MapView';
import SaveSearchModal from '@/components/SaveSearchModal';
import { BackToTopButton } from '@/components/BackToTopButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from '@/components/ui/sheet';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { SlidersHorizontal, Map as MapIcon, Grid3x3, List, Bookmark, X, Crown, MapPin, Users, Bed, Bath, DollarSign, Search, Home as HomeIcon, Image as ImageIcon, Upload, GitCompare, Check, Sparkles, Loader2 } from 'lucide-react';
import { cn, GUEST_OPTIONS } from '@/lib/utils';
import { propertiesAPI, api } from '@/lib/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { usePropertyTranslation } from '@/hooks/usePropertyTranslation';
import { usePageTitle } from '@/hooks/usePageTitle';

// Diamond Property Card with translation - PREMIUM DESIGN
const DiamondPropertyCard = ({ property, txt }) => {
  const { i18n } = useTranslation();
  const { translatedTitle, isLoading } = usePropertyTranslation(property, i18n.language);
  const displayTitle = translatedTitle || property.title;
  const navigate = useNavigate();
  
  return (
    <motion.div 
      className="group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-amber-300/50 hover:border-amber-400 cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ duration: 0.3 }}
      onClick={() => navigate(`/property/${property.id}`)}
    >
      {/* Sparkle Effect Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none" />
      
      {/* Diamond Badge - Premium */}
      <motion.div 
        className="absolute top-4 right-4 z-20 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-amber-900 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg diamond-sparkle"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-bold tracking-wide">DIAMOND</span>
      </motion.div>

      {/* Image - FULL SIZE */}
      <div className="relative h-64 md:h-72 lg:h-80 overflow-hidden">
        <motion.img
          src={property.images?.[0]?.image_url || '/placeholder.jpg'}
          alt={displayTitle}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Price Overlay */}
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-amber-600">${property.price_per_night || property.price || property.nightly_price_suggestion || 0}</span>
              <span className="text-sm text-gray-500">/{txt.perNight}</span>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex gap-2">
            <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-1 shadow-md">
              <Users className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-800">{property.max_guests}</span>
            </div>
            <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-1 shadow-md">
              <Bed className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-800">{property.bedrooms}</span>
            </div>
            <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-1 shadow-md">
              <Bath className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-800">{property.bathrooms}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4 bg-white">
        <h3 className="font-bold text-xl line-clamp-2 text-gray-900 leading-tight">
          {isLoading ? (
            <span className="animate-pulse bg-gray-200 rounded inline-block w-3/4 h-6"></span>
          ) : displayTitle}
        </h3>
        
        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="w-5 h-5 text-amber-500" />
          <span className="text-base">{property.city}, {property.state}</span>
        </div>

        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button 
            className="w-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-600 hover:via-yellow-500 hover:to-amber-600 text-amber-900 font-bold text-base py-6 rounded-xl shadow-lg glow-gold"
            onClick={(e) => { e.stopPropagation(); navigate(`/property/${property.id}`); }}
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {txt.view}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'pt-BR' || i18n.language === 'pt' ? 'pt' : 'en';
  
  // Set page title
  usePageTitle('search');
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [diamondProperties, setDiamondProperties] = useState([]);
  const [diamondLoading, setDiamondLoading] = useState(true);
  const [polygon, setPolygon] = useState(null);
  const [searchLocation, setSearchLocation] = useState(searchParams.get('location') || '');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const debounceTimerRef = useRef(null);
  const resultsRef = useRef(null);
  
  // Translations
  const texts = {
    pt: {
      filters: 'Filtros',
      filtersDescription: 'Filtrar imóveis por diversos critérios',
      map: 'Mapa',
      list: 'Lista',
      saveSearch: 'Salvar Busca',
      compare: 'Comparar',
      results: 'resultados',
      result: 'resultado',
      noResults: 'Nenhum imóvel encontrado',
      tryAdjusting: 'Tente ajustar seus filtros',
      loading: 'Carregando...',
      loadMore: 'Carregar mais',
      diamondHighlight: 'Destaques Diamond',
      priceRange: 'Faixa de preço',
      bedrooms: 'Quartos',
      bathrooms: 'Banheiros',
      guests: 'Hóspedes',
      whereToStay: 'Onde você quer ficar?',
      type: 'Tipo',
      propertyType: 'Tipo de imóvel',
      anyType: 'Qualquer tipo',
      house: 'Casa',
      apartment: 'Apartamento',
      condo: 'Condomínio',
      villa: 'Villa',
      townhouse: 'Sobrado',
      amenities: 'Comodidades',
      pool: 'Piscina',
      wifi: 'WiFi',
      parking: 'Estacionamento',
      airConditioning: 'Ar Condicionado',
      clearFilters: 'Limpar filtros',
      clear: 'Limpar',
      applyFilters: 'Aplicar',
      perNight: 'noite',
      view: 'Ver',
      diamondPremiumText: '\ud83d\udc8e Propriedades Premium com m\u00e1xima visibilidade e recursos exclusivos',
      favorites: 'Favoritos',
      myFavorites: 'Meus Favoritos',
      sortBy: 'Ordenar por',
      relevance: 'Relevância',
      priceLowHigh: 'Preço: menor para maior',
      priceHighLow: 'Preço: maior para menor',
      newest: 'Mais recentes',
      searchByImage: 'Buscar por Foto',
      searchByFilters: 'Buscar por Filtros',
      uploadImage: 'Carregar imagem',
      imageSearchDesc: 'Encontre imóveis semelhantes',
      pricePerNight: 'Preço por Noite',
      minPrice: 'Mín',
      maxPrice: 'Máx',
      numberOfGuests: 'Número de Hóspedes',
      any: 'Qualquer',
      guestsPlus: 'hóspedes',
      specificType: 'Tipo Específico',
      beachHouse: 'Casa de Praia',
      chalet: 'Chalé',
      luxuryApartment: 'Apartamento Luxo',
      penthouse: 'Cobertura',
      cottage: 'Cottage',
      area: 'Área (pés²)',
      minRating: 'Avaliação Mínima',
      cancellationPolicy: 'Política de Cancelamento',
      flexible: 'Flexível',
      moderate: 'Moderada',
      strict: 'Rigorosa',
      houseRules: 'Regras da Casa',
      allowsParties: 'Permite Festas',
      allowsSmoking: 'Permite Fumar',
      kitchen: 'Cozinha',
      washer: 'Lavadora',
      dryer: 'Secadora',
      tv: 'TV',
      gym: 'Academia',
      hotTub: 'Jacuzzi',
      bbqGrill: 'Churrasqueira',
      gameRoom: 'Sala de Jogos',
      petFriendly: 'Aceita Pets',
      wheelchair: 'Acessível',
      evCharger: 'Carregador EV',
      verifiedOnly: 'Apenas Verificados',
      distanceDisney: 'Distância Disney',
      miles: 'milhas',
      hostLanguages: 'Idiomas do Anfitrião',
      portuguese: 'Português',
      english: 'Inglês',
      spanish: 'Espanhol',
      french: 'Francês',
      activeFilters: 'filtro(s) ativo(s)',
      propertiesFound: 'imóveis encontrados',
      propertyFound: 'imóvel encontrado',
      noPropertiesFound: 'Nenhum imóvel encontrado',
      showingOf: 'Mostrando',
      of: 'de',
      propertyLabel: 'imóvel',
      propertiesLabel: 'imóveis',
      showMap: 'Mostrar Mapa',
      hideMap: 'Ocultar Mapa',
      diamondProperties: 'Imóveis Diamond',
      premiumSelection: 'Seleção Premium',
      compareMode: 'Modo Comparação',
      exitMode: 'Sair do Modo',
      property: 'Imóvel',
      properties: 'Imóveis',
      previousPage: 'Voltar',
      homePage: 'Início',
      refresh: 'Atualizar',
      refreshTooltip: 'Atualizar aplicativo e ver últimas mudanças',
      dataRefreshed: 'Dados atualizados com sucesso!',
      errorLoadingProperties: 'Erro ao carregar imóveis',
      errorLoadingMore: 'Erro ao carregar mais imóveis',
      newPropertiesLoaded: 'novos imóveis carregados',
      noSimilarFound: 'Nenhum imóvel similar encontrado',
      similarFound: 'imóvel(is) similar(es) encontrado(s)',
      selectImageFirst: 'Selecione uma imagem primeiro',
      errorSearchingImage: 'Erro ao buscar por imagem. Tente novamente.',
      selectValidImage: 'Por favor, selecione um arquivo de imagem válido',
      imagePasted: 'Imagem colada com sucesso!',
      searchNow: 'Buscar Agora',
      searching: 'Buscando...',
      searchProperty: 'Buscar Imóvel',
      pastePhotoHere: 'Encontre imóveis de outras plataformas no MagicStay',
      imageSearchStep1: '1. Encontre um imóvel em outra plataforma (Airbnb, VRBO, etc)',
      imageSearchStep2: '2. Faça um screenshot e recorte apenas a foto (sem descrição)',
      imageSearchStep3: '3. Salve na sua fototeca e faça upload aqui',
      imageSearchTip: 'Dica: Recorte apenas a imagem do imóvel para melhores resultados',
      imageSearchResults: 'Resultados da Busca por Imagem',
      page: 'Página',
      tryAnotherPhoto: 'Tente com outra foto ou use a busca por filtros'
    },
    en: {
      filters: 'Filters',
      filtersDescription: 'Filter properties by various criteria',
      map: 'Map',
      list: 'List',
      saveSearch: 'Save Search',
      compare: 'Compare',
      results: 'results',
      result: 'result',
      noResults: 'No properties found',
      tryAdjusting: 'Try adjusting your filters',
      loading: 'Loading...',
      loadMore: 'Load more',
      diamondHighlight: 'Diamond Highlights',
      priceRange: 'Price range',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      guests: 'Guests',
      whereToStay: 'Property location',
      type: 'Type',
      propertyType: 'Property type',
      anyType: 'Any type',
      house: 'House',
      apartment: 'Apartment',
      condo: 'Condo',
      villa: 'Villa',
      townhouse: 'Townhouse',
      amenities: 'Amenities',
      pool: 'Pool',
      wifi: 'WiFi',
      parking: 'Parking',
      airConditioning: 'Air Conditioning',
      clearFilters: 'Clear filters',
      clear: 'Clear',
      applyFilters: 'Apply',
      perNight: 'night',
      view: 'View',
      diamondPremiumText: '\ud83d\udc8e Premium properties with maximum visibility and exclusive features',
      favorites: 'Favorites',
      myFavorites: 'My Favorites',
      sortBy: 'Sort by',
      relevance: 'Relevance',
      priceLowHigh: 'Price: low to high',
      priceHighLow: 'Price: high to low',
      newest: 'Newest',
      searchByImage: 'Search by Photo',
      searchByFilters: 'Search by Filters',
      uploadImage: 'Upload image',
      imageSearchDesc: 'Find similar properties',
      pricePerNight: 'Price per Night',
      minPrice: 'Min',
      maxPrice: 'Max',
      numberOfGuests: 'Number of Guests',
      any: 'Any',
      guestsPlus: 'guests',
      specificType: 'Specific Type',
      beachHouse: 'Beach House',
      chalet: 'Chalet',
      luxuryApartment: 'Luxury Apartment',
      penthouse: 'Penthouse',
      cottage: 'Cottage',
      area: 'Area (ft²)',
      minRating: 'Minimum Rating',
      cancellationPolicy: 'Cancellation Policy',
      flexible: 'Flexible',
      moderate: 'Moderate',
      strict: 'Strict',
      houseRules: 'House Rules',
      allowsParties: 'Allows Parties',
      allowsSmoking: 'Allows Smoking',
      kitchen: 'Kitchen',
      washer: 'Washer',
      dryer: 'Dryer',
      tv: 'TV',
      gym: 'Gym',
      hotTub: 'Hot Tub',
      bbqGrill: 'BBQ Grill',
      gameRoom: 'Game Room',
      petFriendly: 'Pet Friendly',
      wheelchair: 'Wheelchair Accessible',
      evCharger: 'EV Charger',
      verifiedOnly: 'Verified Only',
      distanceDisney: 'Distance to Disney',
      miles: 'miles',
      hostLanguages: 'Host Languages',
      portuguese: 'Portuguese',
      english: 'English',
      spanish: 'Spanish',
      french: 'French',
      activeFilters: 'active filter(s)',
      propertiesFound: 'properties found',
      propertyFound: 'property found',
      noPropertiesFound: 'No properties found',
      showingOf: 'Showing',
      of: 'of',
      propertyLabel: 'property',
      propertiesLabel: 'properties',
      showMap: 'Show Map',
      hideMap: 'Hide Map',
      diamondProperties: 'Diamond Properties',
      premiumSelection: 'Premium Selection',
      compareMode: 'Compare Mode',
      exitMode: 'Exit Mode',
      property: 'Property',
      properties: 'Properties',
      previousPage: 'Back',
      homePage: 'Home',
      refresh: 'Refresh',
      refreshTooltip: 'Refresh app and see latest changes',
      dataRefreshed: 'Data refreshed successfully!',
      errorLoadingProperties: 'Error loading properties',
      errorLoadingMore: 'Error loading more properties',
      newPropertiesLoaded: 'new properties loaded',
      noSimilarFound: 'No similar properties found',
      similarFound: 'similar property(ies) found',
      selectImageFirst: 'Select an image first',
      errorSearchingImage: 'Error searching by image. Try again.',
      selectValidImage: 'Please select a valid image file',
      imagePasted: 'Image pasted successfully!',
      searchNow: 'Search Now',
      searching: 'Searching...',
      searchProperty: 'Search Property',
      pastePhotoHere: 'Find properties from other platforms on MagicStay',
      imageSearchStep1: '1. Find a property on another platform (Airbnb, VRBO, etc)',
      imageSearchStep2: '2. Take a screenshot and crop only the photo (no description)',
      imageSearchStep3: '3. Save to your photo library and upload here',
      imageSearchTip: 'Tip: Crop only the property image for best results',
      imageSearchResults: 'Image Search Results',
      page: 'Page',
      tryAnotherPhoto: 'Try another photo or use the filter search'
    }
  };
  const txt = texts[language] || texts.en;
  
  // Image Search States
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [hasSearchedImage, setHasSearchedImage] = useState(false); // Bug 21 fix: Track if search was performed
  const fileInputRef = useRef(null);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Comparison States
  const [compareMode, setCompareMode] = useState(false);
  const [compareCount, setCompareCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('magicstay_token');
    setIsLoggedIn(!!token);
  }, []);
  
  // Monitor compare list
  useEffect(() => {
    const updateCompareCount = () => {
      const compareList = JSON.parse(localStorage.getItem('compare_properties') || '[]');
      setCompareCount(compareList.length);
    };
    
    updateCompareCount();
    window.addEventListener('storage', updateCompareCount);
    
    return () => window.removeEventListener('storage', updateCompareCount);
  }, []);
  
  // Monitor favorites list
  useEffect(() => {
    const updateFavoritesCount = () => {
      try {
        const favoritesList = JSON.parse(localStorage.getItem('favorites') || '[]');
        setFavoritesCount(favoritesList.length);
      } catch (error) {
        console.error('Error reading favorites:', error);
        setFavoritesCount(0);
      }
    };
    
    updateFavoritesCount();
    
    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', updateFavoritesCount);
    
    // Listen for custom event when favorites change in same tab
    window.addEventListener('favoritesChanged', updateFavoritesCount);
    
    return () => {
      window.removeEventListener('storage', updateFavoritesCount);
      window.removeEventListener('favoritesChanged', updateFavoritesCount);
    };
  }, []);
  
  const handleCompareUpdate = () => {
    const compareList = JSON.parse(localStorage.getItem('compare_properties') || '[]');
    setCompareCount(compareList.length);
  };
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    // Check URL search params first (more reliable on initial load)
    const urlGuests = searchParams.get('guests');
    const urlBedrooms = searchParams.get('bedrooms');
    const urlBathrooms = searchParams.get('bathrooms');
    const urlPropertyType = searchParams.get('property_type');
    const urlLocation = searchParams.get('location');
    const urlMinPrice = searchParams.get('min_price');
    const urlMaxPrice = searchParams.get('max_price');
    
    // Check URL params
    if (urlLocation && urlLocation.trim() !== '') return true;
    if (urlGuests) return true;
    if (urlBedrooms) return true;
    if (urlBathrooms) return true;
    if (urlPropertyType) return true;
    if (urlMinPrice || urlMaxPrice) return true;
    
    // Check if location filter is active
    if (searchLocation && searchLocation.trim() !== '') return true;
    
    // Check price range (not default)
    if (priceRange[0] !== 0 || priceRange[1] !== 10000) return true;
    
    // Check basic filters
    if (guests !== 'any') return true;
    if (bedrooms !== 'any') return true;
    if (bathrooms !== 'any') return true;
    if (propertyType !== 'any') return true;
    if (accommodationSubtype !== 'any') return true;
    if (minRating !== 'any') return true;
    if (cancellationPolicy !== 'any') return true;
    if (verifiedOnly) return true;
    
    // Check square feet range (not default)
    if (squareFeetRange[0] !== 0 || squareFeetRange[1] !== 10000) return true;
    
    // Check distance (not default)
    if (maxDistanceToDisney !== 30) return true;
    
    // Check house rules
    if (allowsParties) return true;
    if (allowsSmoking) return true;
    
    // Check amenities
    if (hasPool || hasWifi || hasParking || hasAc || hasKitchen || hasWasher || 
        hasDryer || hasTv || hasGym || hasHotTub || hasBbqGrill || hasGameRoom || 
        hasPetFriendly || hasWheelchairAccessible || hasEvCharger) return true;
    
    // Check host languages
    if (hostLanguages.length > 0) return true;
    
    return false;
  };
  
  // Basic Filters
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [guests, setGuests] = useState('any');
  const [bedrooms, setBedrooms] = useState('any');
  const [bathrooms, setBathrooms] = useState('any');
  const [propertyType, setPropertyType] = useState('any');
  const [accommodationSubtype, setAccommodationSubtype] = useState('any');
  const [squareFeetRange, setSquareFeetRange] = useState([0, 10000]);  // Default 0 = no minimum filter
  const [minRating, setMinRating] = useState('any');
  const [cancellationPolicy, setCancellationPolicy] = useState('any');
  const [hostLanguages, setHostLanguages] = useState([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [maxDistanceToDisney, setMaxDistanceToDisney] = useState(30);
  
  // House Rules
  const [allowsParties, setAllowsParties] = useState(false);
  const [allowsSmoking, setAllowsSmoking] = useState(false);
  
  // Amenities filters
  const [hasPool, setHasPool] = useState(false);
  const [hasWifi, setHasWifi] = useState(false);
  const [hasParking, setHasParking] = useState(false);
  const [hasAc, setHasAc] = useState(false);
  const [hasKitchen, setHasKitchen] = useState(false);
  const [hasWasher, setHasWasher] = useState(false);
  const [hasDryer, setHasDryer] = useState(false);
  const [hasTv, setHasTv] = useState(false);
  const [hasGym, setHasGym] = useState(false);
  const [hasHotTub, setHasHotTub] = useState(false);
  const [hasBbqGrill, setHasBbqGrill] = useState(false);
  const [hasGameRoom, setHasGameRoom] = useState(false);
  const [hasPetFriendly, setHasPetFriendly] = useState(false);
  const [hasWheelchairAccessible, setHasWheelchairAccessible] = useState(false);
  const [hasEvCharger, setHasEvCharger] = useState(false);
  
  // ========== SYNC ALL FILTERS FROM URL ON MOUNT ==========
  useEffect(() => {
    // Read filters from URL and set state
    const urlLocation = searchParams.get('location');
    const urlGuests = searchParams.get('guests');
    const urlBedrooms = searchParams.get('bedrooms');
    const urlBathrooms = searchParams.get('bathrooms');
    const urlPropertyType = searchParams.get('property_type');
    const urlAccommodationSubtype = searchParams.get('accommodation_subtype');
    const urlMinPrice = searchParams.get('min_price');
    const urlMaxPrice = searchParams.get('max_price');
    const urlMinSqft = searchParams.get('min_sqft');
    const urlMaxSqft = searchParams.get('max_sqft');
    const urlMinRating = searchParams.get('min_rating');
    const urlCancellationPolicy = searchParams.get('cancellation_policy');
    const urlHostLanguages = searchParams.get('host_languages');
    const urlMaxDisneyDistance = searchParams.get('max_disney_distance');
    
    // Basic filters
    if (urlLocation) setSearchLocation(urlLocation);
    if (urlGuests) setGuests(urlGuests);
    if (urlBedrooms) setBedrooms(urlBedrooms);
    if (urlBathrooms) setBathrooms(urlBathrooms);
    if (urlPropertyType) setPropertyType(urlPropertyType);
    if (urlAccommodationSubtype) setAccommodationSubtype(urlAccommodationSubtype);
    if (urlMinRating) setMinRating(urlMinRating);
    if (urlCancellationPolicy) setCancellationPolicy(urlCancellationPolicy);
    if (urlHostLanguages) setHostLanguages(urlHostLanguages.split(','));
    if (urlMaxDisneyDistance) setMaxDistanceToDisney(parseInt(urlMaxDisneyDistance));
    
    // Price range
    if (urlMinPrice || urlMaxPrice) {
      setPriceRange([
        urlMinPrice ? parseInt(urlMinPrice) : 0,
        urlMaxPrice ? parseInt(urlMaxPrice) : 10000
      ]);
    }
    
    // Square feet range
    if (urlMinSqft || urlMaxSqft) {
      setSquareFeetRange([
        urlMinSqft ? parseInt(urlMinSqft) : 0,
        urlMaxSqft ? parseInt(urlMaxSqft) : 10000
      ]);
    }
    
    // Boolean filters - amenities
    if (searchParams.get('has_pool') === 'true') setHasPool(true);
    if (searchParams.get('has_wifi') === 'true') setHasWifi(true);
    if (searchParams.get('has_parking') === 'true') setHasParking(true);
    if (searchParams.get('has_ac') === 'true') setHasAc(true);
    if (searchParams.get('has_kitchen') === 'true') setHasKitchen(true);
    if (searchParams.get('has_washer') === 'true') setHasWasher(true);
    if (searchParams.get('has_dryer') === 'true') setHasDryer(true);
    if (searchParams.get('has_tv') === 'true') setHasTv(true);
    if (searchParams.get('has_gym') === 'true') setHasGym(true);
    if (searchParams.get('has_hot_tub') === 'true') setHasHotTub(true);
    if (searchParams.get('has_bbq_grill') === 'true') setHasBbqGrill(true);
    if (searchParams.get('has_game_room') === 'true') setHasGameRoom(true);
    if (searchParams.get('has_pet_friendly') === 'true') setHasPetFriendly(true);
    if (searchParams.get('has_wheelchair_accessible') === 'true') setHasWheelchairAccessible(true);
    if (searchParams.get('has_ev_charger') === 'true') setHasEvCharger(true);
    if (searchParams.get('allows_parties') === 'true') setAllowsParties(true);
    if (searchParams.get('allows_smoking') === 'true') setAllowsSmoking(true);
    if (searchParams.get('verified_only') === 'true') setVerifiedOnly(true);
  }, []); // Only on mount
  
  // ========== SYNC ALL FILTERS TO URL ==========
  const updateUrlWithFilters = () => {
    const params = new URLSearchParams();
    
    // Basic filters
    if (searchLocation) params.set('location', searchLocation);
    if (guests !== 'any') params.set('guests', guests);
    if (bedrooms !== 'any') params.set('bedrooms', bedrooms);
    if (bathrooms !== 'any') params.set('bathrooms', bathrooms);
    if (propertyType !== 'any') params.set('property_type', propertyType);
    if (accommodationSubtype !== 'any') params.set('accommodation_subtype', accommodationSubtype);
    if (minRating !== 'any') params.set('min_rating', minRating);
    if (cancellationPolicy !== 'any') params.set('cancellation_policy', cancellationPolicy);
    if (hostLanguages.length > 0) params.set('host_languages', hostLanguages.join(','));
    
    // Price range
    if (priceRange[0] > 0) params.set('min_price', priceRange[0].toString());
    if (priceRange[1] < 10000) params.set('max_price', priceRange[1].toString());
    
    // Square feet range
    if (squareFeetRange[0] > 0) params.set('min_sqft', squareFeetRange[0].toString());
    if (squareFeetRange[1] < 10000) params.set('max_sqft', squareFeetRange[1].toString());
    
    // Distance to Disney
    if (maxDistanceToDisney < 30) params.set('max_disney_distance', maxDistanceToDisney.toString());
    
    // Boolean filters - amenities
    if (hasPool) params.set('has_pool', 'true');
    if (hasWifi) params.set('has_wifi', 'true');
    if (hasParking) params.set('has_parking', 'true');
    if (hasAc) params.set('has_ac', 'true');
    if (hasKitchen) params.set('has_kitchen', 'true');
    if (hasWasher) params.set('has_washer', 'true');
    if (hasDryer) params.set('has_dryer', 'true');
    if (hasTv) params.set('has_tv', 'true');
    if (hasGym) params.set('has_gym', 'true');
    if (hasHotTub) params.set('has_hot_tub', 'true');
    if (hasBbqGrill) params.set('has_bbq_grill', 'true');
    if (hasGameRoom) params.set('has_game_room', 'true');
    if (hasPetFriendly) params.set('has_pet_friendly', 'true');
    if (hasWheelchairAccessible) params.set('has_wheelchair_accessible', 'true');
    if (hasEvCharger) params.set('has_ev_charger', 'true');
    if (allowsParties) params.set('allows_parties', 'true');
    if (allowsSmoking) params.set('allows_smoking', 'true');
    if (verifiedOnly) params.set('verified_only', 'true');
    
    // Update URL without full page reload
    setSearchParams(params, { replace: true });
  };
  
  // Auto-sync filters to URL when they change
  useEffect(() => {
    // Debounce URL updates to avoid too many history entries
    const timeoutId = setTimeout(() => {
      updateUrlWithFilters();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [
    searchLocation, guests, bedrooms, bathrooms, propertyType, accommodationSubtype,
    priceRange, squareFeetRange, minRating, cancellationPolicy, hostLanguages,
    hasPool, hasWifi, hasParking, hasAc, hasKitchen, hasWasher, hasDryer, hasTv,
    hasGym, hasHotTub, hasBbqGrill, hasGameRoom, hasPetFriendly, hasWheelchairAccessible,
    hasEvCharger, allowsParties, allowsSmoking, verifiedOnly, maxDistanceToDisney
  ]);

  // Load properties from saved search if available
  useEffect(() => {
    if (location.state?.properties && location.state?.fromSavedSearch) {
      setProperties(location.state.properties);
      setLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (!location.state?.fromSavedSearch) {
        fetchProperties();
      }
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    priceRange, guests, bedrooms, bathrooms, propertyType, accommodationSubtype,
    squareFeetRange, minRating, cancellationPolicy, hostLanguages, hasPool,
    verifiedOnly, maxDistanceToDisney, hasWifi, hasParking, hasAc, hasKitchen,
    hasWasher, hasDryer, hasTv, hasGym, hasHotTub, hasBbqGrill, hasGameRoom,
    hasPetFriendly, hasWheelchairAccessible, hasEvCharger, allowsParties, allowsSmoking,
    polygon, searchParams
  ]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      
      // Get search params from URL
      const locationParam = searchParams.get('location');
      const guestsParam = searchParams.get('guests');
      
      // Use location filter with exact city match to avoid showing all properties
      if (searchLocation && searchLocation.trim() !== '') params.location = searchLocation;
      else if (locationParam) params.location = locationParam;
      
      if (guests !== 'any') params.guests = parseInt(guests);
      else if (guestsParam) params.guests = parseInt(guestsParam);
      
      if (bedrooms !== 'any') params.bedrooms = parseInt(bedrooms);
      if (bathrooms !== 'any') params.bathrooms = parseInt(bathrooms);
      if (propertyType !== 'any') params.property_type = propertyType;
      if (accommodationSubtype !== 'any') params.accommodation_subtype = accommodationSubtype;
      
      // Square feet filter - only apply when user explicitly sets a value
      if (squareFeetRange[0] > 0) params.min_square_feet = squareFeetRange[0];
      if (squareFeetRange[1] < 10000) params.max_square_feet = squareFeetRange[1];
      
      // Rating filter
      if (minRating !== 'any') params.min_rating = parseFloat(minRating);
      
      // Cancellation policy
      if (cancellationPolicy !== 'any') params.cancellation_policy = cancellationPolicy;
      
      // Host languages
      if (hostLanguages.length > 0) params.host_languages = hostLanguages.join(',');
      
      // House rules
      if (allowsParties) params.allows_parties = true;
      if (allowsSmoking) params.allows_smoking = true;
      
      // Amenities
      if (hasPool) params.has_pool = true;
      if (verifiedOnly) params.verified_only = true;
      if (maxDistanceToDisney < 30) params.max_distance_to_disney = maxDistanceToDisney;
      
      if (hasWifi) params.has_wifi = true;
      if (hasParking) params.has_parking = true;
      if (hasAc) params.has_ac = true;
      if (hasKitchen) params.has_kitchen = true;
      if (hasWasher) params.has_washer = true;
      if (hasDryer) params.has_dryer = true;
      if (hasTv) params.has_tv = true;
      if (hasGym) params.has_gym = true;
      if (hasHotTub) params.has_hot_tub = true;
      if (hasBbqGrill) params.has_bbq_grill = true;
      if (hasGameRoom) params.has_game_room = true;
      if (hasPetFriendly) params.has_pet_friendly = true;
      if (hasWheelchairAccessible) params.has_wheelchair_accessible = true;
      if (hasEvCharger) params.has_ev_charger = true;
      
      params.min_price = priceRange[0];
      params.max_price = priceRange[1];
      
      // Add pagination params
      params.page = 1;
      params.page_size = 20;
      
      const response = await propertiesAPI.getAll(params);
      
      // Handle new pagination response format
      if (response.data.properties) {
        setProperties(response.data.properties);
        setCurrentPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.total_pages);
        setTotalProperties(response.data.pagination.total);
        setHasMorePages(response.data.pagination.has_next);
      } else {
        // Fallback for old format (without pagination)
        setProperties(response.data);
      }
    } catch (error) {
      // Only show error for real errors, not cancelled requests
      if (error.code !== 'ERR_CANCELED' && 
          error.name !== 'AbortError' && 
          !error.message?.includes('canceled')) {
        console.error('Error fetching properties:', error);
        toast.error(txt.errorLoadingProperties);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => {
    setPriceRange([0, 10000]);
    setGuests('any');
    setBedrooms('any');
    setBathrooms('any');
    setPropertyType('any');
    setAccommodationSubtype('any');
    setSquareFeetRange([0, 10000]);
    setMinRating('any');
    setCancellationPolicy('any');
    setHostLanguages([]);
    setHasPool(false);
    setVerifiedOnly(false);
    setMaxDistanceToDisney(30);
    setHasWifi(false);
    setHasParking(false);
    setHasAc(false);
    setHasKitchen(false);
    setHasWasher(false);
    setHasDryer(false);
    setHasTv(false);
    setHasGym(false);
    setHasHotTub(false);
    setHasBbqGrill(false);
    setHasGameRoom(false);
    setHasPetFriendly(false);
    setHasWheelchairAccessible(false);
    setHasEvCharger(false);
    setAllowsParties(false);
    setAllowsSmoking(false);
    setPolygon(null);
  };

  const fetchDiamondProperties = async () => {
    try {
      setDiamondLoading(true);
      const response = await api.get('/properties/diamond?limit=10');
      setDiamondProperties(response.data || []);
    } catch (error) {
      // Only log real errors
      if (error.code !== 'ERR_CANCELED' && error.name !== 'AbortError') {
        console.error('Error fetching diamond properties:', error);
      }
    } finally {
      setDiamondLoading(false);
    }
  };

  useEffect(() => {
    fetchDiamondProperties();
  }, []);

  // Image Search Functions
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(txt.selectValidImage);
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImagePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          setSelectedImage(file);
          const reader = new FileReader();
          reader.onload = (e) => setImagePreview(e.target.result);
          reader.readAsDataURL(file);
          toast.success(txt.imagePasted);
        }
      }
    }
  };

  const searchByImage = async () => {
    if (!selectedImage) {
      toast.error(txt.selectImageFirst);
      return;
    }

    try {
      setImageSearchLoading(true);
      setHasSearchedImage(true); // Bug 21 fix: Mark that search was attempted
      
      const formData = new FormData();
      formData.append('file', selectedImage);

      const response = await api.post('/search/by-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImageSearchResults(response.data.properties || []);
      
      if (response.data.count === 0) {
        toast.info(txt.noSimilarFound);
      } else {
        toast.success(`${response.data.count} ${txt.similarFound}`);
      }
    } catch (error) {
      console.error('Error searching by image:', error);
      toast.error(txt.errorSearchingImage);
    } finally {
      setImageSearchLoading(false);
    }
  };

  const clearImageSearch = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageSearchResults([]);
    setHasSearchedImage(false); // Bug 21 fix: Reset search state
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Load More Properties
  const loadMoreProperties = async () => {
    if (!hasMorePages || loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      // Build params (same as fetchProperties)
      const params = {};
      if (searchLocation) params.location = searchLocation;
      if (guests !== 'any') params.guests = parseInt(guests);
      if (bedrooms !== 'any') params.bedrooms = parseInt(bedrooms);
      if (bathrooms !== 'any') params.bathrooms = parseInt(bathrooms);
      if (propertyType !== 'any') params.property_type = propertyType;
      
      if (hasPool) params.has_pool = true;
      if (verifiedOnly) params.verified_only = true;
      if (maxDistanceToDisney < 30) params.max_distance_to_disney = maxDistanceToDisney;
      if (hasWifi) params.has_wifi = true;
      if (hasParking) params.has_parking = true;
      if (hasAc) params.has_ac = true;
      if (hasKitchen) params.has_kitchen = true;
      if (hasWasher) params.has_washer = true;
      if (hasDryer) params.has_dryer = true;
      if (hasTv) params.has_tv = true;
      if (hasGym) params.has_gym = true;
      if (hasHotTub) params.has_hot_tub = true;
      if (hasBbqGrill) params.has_bbq_grill = true;
      if (hasGameRoom) params.has_game_room = true;
      if (hasPetFriendly) params.has_pet_friendly = true;
      if (hasWheelchairAccessible) params.has_wheelchair_accessible = true;
      if (hasEvCharger) params.has_ev_charger = true;
      
      params.min_price = priceRange[0];
      params.max_price = priceRange[1];
      
      // Next page
      params.page = currentPage + 1;
      params.page_size = 20;
      
      const response = await propertiesAPI.getAll(params);
      
      if (response.data.properties) {
        // Append new properties to existing ones
        setProperties(prev => [...prev, ...response.data.properties]);
        setCurrentPage(response.data.pagination.page);
        setHasMorePages(response.data.pagination.has_next);
        
        toast.success(`${response.data.properties.length} ${txt.newPropertiesLoaded}`);
      }
    } catch (error) {
      // Only show error for real errors
      if (error.code !== 'ERR_CANCELED' && error.name !== 'AbortError') {
        console.error('Error loading more properties:', error);
        toast.error(txt.errorLoadingMore);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // Auto-play for Diamond carousel
  const [carouselApi, setCarouselApi] = useState(null);
  
  useEffect(() => {
    if (!carouselApi || diamondProperties.length === 0) return;

    const autoPlay = setInterval(() => {
      carouselApi.scrollNext();
    }, 5000);

    return () => clearInterval(autoPlay);
  }, [carouselApi, diamondProperties]);

  const getActiveFilterCount = () => {
    let count = 0;
    if (priceRange[0] > 0 || priceRange[1] < 500) count++;
    if (guests !== 'any') count++;
    if (bedrooms !== 'any') count++;
    if (bathrooms !== 'any') count++;
    if (propertyType !== 'any') count++;
    if (accommodationSubtype !== 'any') count++;
    if (squareFeetRange[0] > 0 || squareFeetRange[1] < 10000) count++;
    if (minRating !== 'any') count++;
    if (cancellationPolicy !== 'any') count++;
    if (hostLanguages.length > 0) count++;
    if (hasPool) count++;
    if (verifiedOnly) count++;
    if (maxDistanceToDisney < 30) count++;
    if (hasWifi) count++;
    if (hasParking) count++;
    if (hasAc) count++;
    if (hasKitchen) count++;
    if (hasWasher) count++;
    if (hasDryer) count++;
    if (hasTv) count++;
    if (hasGym) count++;
    if (hasHotTub) count++;
    if (hasBbqGrill) count++;
    if (hasGameRoom) count++;
    if (hasPetFriendly) count++;
    if (hasWheelchairAccessible) count++;
    if (hasEvCharger) count++;
    if (allowsParties) count++;
    if (allowsSmoking) count++;
    if (polygon) count++;
    return count;
  };

  const handleSaveSearch = () => {
    const token = localStorage.getItem('magicstay_token');
    if (!token) {
      toast.error(language === 'pt' ? 'Faça login para salvar buscas' : 'Login to save searches');
      return;
    }
    setSaveModalOpen(true);
  };

  const getCurrentFilters = () => {
    const filters = {
      location: searchParams.get('location') || '',
      min_price: priceRange[0],
      max_price: priceRange[1]
    };
    
    if (guests !== 'any') filters.guests = guests;
    if (bedrooms !== 'any') filters.bedrooms = bedrooms;
    if (bathrooms !== 'any') filters.bathrooms = bathrooms;
    if (propertyType !== 'any') filters.property_type = propertyType;
    if (accommodationSubtype !== 'any') filters.accommodation_subtype = accommodationSubtype;
    if (minRating !== 'any') filters.min_rating = minRating;
    if (cancellationPolicy !== 'any') filters.cancellation_policy = cancellationPolicy;
    if (hostLanguages.length > 0) filters.host_languages = hostLanguages;
    
    // Add all boolean filters
    const boolFilters = {
      has_pool: hasPool,
      verified_only: verifiedOnly,
      has_wifi: hasWifi,
      has_parking: hasParking,
      has_ac: hasAc,
      has_kitchen: hasKitchen,
      has_washer: hasWasher,
      has_dryer: hasDryer,
      has_tv: hasTv,
      has_gym: hasGym,
      has_hot_tub: hasHotTub,
      has_bbq_grill: hasBbqGrill,
      has_game_room: hasGameRoom,
      has_pet_friendly: hasPetFriendly,
      has_wheelchair_accessible: hasWheelchairAccessible,
      has_ev_charger: hasEvCharger,
      allows_parties: allowsParties,
      allows_smoking: allowsSmoking
    };
    
    Object.entries(boolFilters).forEach(([key, value]) => {
      if (value) filters[key] = value;
    });
    
    return filters;
  };

  const FilterPanel = () => (
    <div className="space-y-6" data-testid="filters-panel">
      {/* Active Filters Header */}
      {getActiveFilterCount() > 0 && (
        <div className="flex items-center justify-between pb-4 border-b">
          <Badge variant="secondary" className="text-sm">
            {getActiveFilterCount()} {txt.activeFilters}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            data-testid="clear-filters-button"
          >
            <X className="w-4 h-4 mr-1" />
            {txt.clear}
          </Button>
        </div>
      )}

      {/* Price Range */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.pricePerNight}</Label>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
            <Input
              type="number"
              min="0"
              placeholder={txt.minPrice || "Mín"}
              value={priceRange[0] || ''}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 0) {
                  setPriceRange([val, priceRange[1]]);
                } else if (e.target.value === '') {
                  setPriceRange([0, priceRange[1]]);
                }
              }}
              onBlur={(e) => {
                const val = Math.max(0, parseInt(e.target.value) || 0);
                setPriceRange([val, priceRange[1]]);
              }}
              className="pl-7 h-10 sm:h-9 text-sm w-full"
              data-testid="filter-price-min"
            />
          </div>
          <span className="text-gray-400 text-center hidden sm:block">-</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
            <Input
              type="number"
              min="0"
              placeholder={txt.maxPrice || "Máx"}
              value={priceRange[1] === 10000 ? '' : priceRange[1]}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 0) {
                  setPriceRange([priceRange[0], val]);
                } else if (e.target.value === '') {
                  setPriceRange([priceRange[0], 10000]);
                }
              }}
              onBlur={(e) => {
                const val = Math.max(0, parseInt(e.target.value) || 10000);
                setPriceRange([priceRange[0], val]);
              }}
              className="pl-7 h-10 sm:h-9 text-sm w-full"
              data-testid="filter-price-max"
            />
          </div>
        </div>
      </div>

      {/* Number of Guests */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.numberOfGuests}</Label>
        <Select value={guests} onValueChange={setGuests}>
          <SelectTrigger data-testid="filter-guests-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GUEST_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {language === 'pt' ? option.label.pt : option.label.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Property Type */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.propertyType}</Label>
        <Select value={propertyType} onValueChange={setPropertyType}>
          <SelectTrigger data-testid="filter-property-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{txt.any}</SelectItem>
            <SelectItem value="house">{txt.house}</SelectItem>
            <SelectItem value="apartment">{txt.apartment}</SelectItem>
            <SelectItem value="condo">{txt.condo}</SelectItem>
            <SelectItem value="villa">{txt.villa}</SelectItem>
            <SelectItem value="townhouse">{txt.townhouse}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Accommodation Subtype */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.specificType}</Label>
        <Select value={accommodationSubtype} onValueChange={setAccommodationSubtype}>
          <SelectTrigger data-testid="filter-subtype-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{txt.any}</SelectItem>
            <SelectItem value="beach_house">{txt.beachHouse}</SelectItem>
            <SelectItem value="chalet">{txt.chalet}</SelectItem>
            <SelectItem value="luxury_apartment">{txt.luxuryApartment}</SelectItem>
            <SelectItem value="penthouse">{txt.penthouse}</SelectItem>
            <SelectItem value="cottage">{txt.cottage}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Square Feet Range */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.area}</Label>
        <Slider
          value={squareFeetRange}
          onValueChange={setSquareFeetRange}
          min={0}
          max={10000}
          step={100}
          className="mt-2"
          data-testid="filter-sqft-slider"
        />
        <div className="flex justify-between mt-2 text-sm text-muted-foreground">
          <span>{squareFeetRange[0] === 0 ? txt.any || 'Qualquer' : `${squareFeetRange[0]} ft²`}</span>
          <span>{squareFeetRange[1]} ft²</span>
        </div>
      </div>

      {/* Bedrooms */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.bedrooms}</Label>
        <Select value={bedrooms} onValueChange={setBedrooms}>
          <SelectTrigger data-testid="filter-bedrooms-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{txt.any}</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
            <SelectItem value="6">6+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bathrooms */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.bathrooms}</Label>
        <Select value={bathrooms} onValueChange={setBathrooms}>
          <SelectTrigger data-testid="filter-bathrooms-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{txt.any}</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Minimum Rating */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.minRating}</Label>
        <Select value={minRating} onValueChange={setMinRating}>
          <SelectTrigger data-testid="filter-rating-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{txt.any}</SelectItem>
            <SelectItem value="3.0">3.0+ ⭐</SelectItem>
            <SelectItem value="3.5">3.5+ ⭐</SelectItem>
            <SelectItem value="4.0">4.0+ ⭐</SelectItem>
            <SelectItem value="4.5">4.5+ ⭐</SelectItem>
            <SelectItem value="4.8">4.8+ ⭐⭐</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cancellation Policy */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.cancellationPolicy}</Label>
        <Select value={cancellationPolicy} onValueChange={setCancellationPolicy}>
          <SelectTrigger data-testid="filter-cancellation-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{txt.any}</SelectItem>
            <SelectItem value="flexible">{txt.flexible}</SelectItem>
            <SelectItem value="moderate">{txt.moderate}</SelectItem>
            <SelectItem value="strict">{txt.strict}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Distance to Disney */}
      <div>
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.distanceDisney}</Label>
        <Slider
          value={[maxDistanceToDisney]}
          onValueChange={(val) => setMaxDistanceToDisney(val[0])}
          max={30}
          min={1}
          step={1}
          className="mt-2"
          data-testid="filter-distance-slider"
        />
        <div className="mt-2 text-sm text-muted-foreground">
          Até {maxDistanceToDisney} milhas
        </div>
      </div>

      {/* House Rules Section */}
      <div className="pt-4 border-t">
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.houseRules}</Label>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="allows-parties" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.allowsParties}
            </Label>
            <Switch
              id="allows-parties"
              checked={allowsParties}
              onCheckedChange={setAllowsParties}
              data-testid="filter-parties-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="allows-smoking" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.allowsSmoking}
            </Label>
            <Switch
              id="allows-smoking"
              checked={allowsSmoking}
              onCheckedChange={setAllowsSmoking}
              data-testid="filter-smoking-switch"
            />
          </div>
        </div>
      </div>

      {/* Amenities Section */}
      <div className="pt-4 border-t">
        <Label className="text-sm font-semibold mb-3 block text-gray-900">{txt.amenities}</Label>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="pool" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.pool}
            </Label>
            <Switch
              id="pool"
              checked={hasPool}
              onCheckedChange={setHasPool}
              data-testid="filter-pool-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="wifi" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.wifi}
            </Label>
            <Switch
              id="wifi"
              checked={hasWifi}
              onCheckedChange={setHasWifi}
              data-testid="filter-wifi-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="parking" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.parking}
            </Label>
            <Switch
              id="parking"
              checked={hasParking}
              onCheckedChange={setHasParking}
              data-testid="filter-parking-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="ac" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.airConditioning}
            </Label>
            <Switch
              id="ac"
              checked={hasAc}
              onCheckedChange={setHasAc}
              data-testid="filter-ac-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="kitchen" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.kitchen}
            </Label>
            <Switch
              id="kitchen"
              checked={hasKitchen}
              onCheckedChange={setHasKitchen}
              data-testid="filter-kitchen-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="washer" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.washer}
            </Label>
            <Switch
              id="washer"
              checked={hasWasher}
              onCheckedChange={setHasWasher}
              data-testid="filter-washer-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="dryer" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.dryer}
            </Label>
            <Switch
              id="dryer"
              checked={hasDryer}
              onCheckedChange={setHasDryer}
              data-testid="filter-dryer-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="tv" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.tv}
            </Label>
            <Switch
              id="tv"
              checked={hasTv}
              onCheckedChange={setHasTv}
              data-testid="filter-tv-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="gym" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.gym}
            </Label>
            <Switch
              id="gym"
              checked={hasGym}
              onCheckedChange={setHasGym}
              data-testid="filter-gym-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="hot-tub" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.hotTub}
            </Label>
            <Switch
              id="hot-tub"
              checked={hasHotTub}
              onCheckedChange={setHasHotTub}
              data-testid="filter-hot-tub-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="bbq-grill" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.bbqGrill}
            </Label>
            <Switch
              id="bbq-grill"
              checked={hasBbqGrill}
              onCheckedChange={setHasBbqGrill}
              data-testid="filter-bbq-grill-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="game-room" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.gameRoom}
            </Label>
            <Switch
              id="game-room"
              checked={hasGameRoom}
              onCheckedChange={setHasGameRoom}
              data-testid="filter-game-room-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="pet-friendly" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.petFriendly}
            </Label>
            <Switch
              id="pet-friendly"
              checked={hasPetFriendly}
              onCheckedChange={setHasPetFriendly}
              data-testid="filter-pet-friendly-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="wheelchair" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.wheelchair}
            </Label>
            <Switch
              id="wheelchair"
              checked={hasWheelchairAccessible}
              onCheckedChange={setHasWheelchairAccessible}
              data-testid="filter-wheelchair-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="ev-charger" className="text-sm font-medium cursor-pointer text-gray-700">
              {txt.evCharger}
            </Label>
            <Switch
              id="ev-charger"
              checked={hasEvCharger}
              onCheckedChange={setHasEvCharger}
              data-testid="filter-ev-charger-switch"
            />
          </div>
        </div>
      </div>

      {/* Verification */}
      <div className="pt-4 border-t">
        <div className="flex items-center justify-between">
          <Label htmlFor="verified-only" className="text-sm font-medium cursor-pointer text-gray-700">
            {txt.verifiedOnly}
          </Label>
          <Switch
            id="verified-only"
            checked={verifiedOnly}
            onCheckedChange={setVerifiedOnly}
            data-testid="filter-verified-switch"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <Navbar />

      {/* Navigation Buttons */}
      <div className="bg-background border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            <NavigationButtons className="" />
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // Refresh data without page reload - more reliable approach
                setLoading(true);
                setDiamondLoading(true);
                
                try {
                  // Fetch both in parallel
                  const [propertiesPromise, diamondPromise] = await Promise.allSettled([
                    (async () => {
                      const params = {
                        min_price: priceRange[0],
                        max_price: priceRange[1],
                        page: 1,
                        page_size: 20
                      };
                      
                      if (guests !== 'any') params.guests = parseInt(guests);
                      if (bedrooms !== 'any') params.bedrooms = parseInt(bedrooms);
                      if (bathrooms !== 'any') params.bathrooms = parseInt(bathrooms);
                      if (propertyType !== 'any') params.property_type = propertyType;
                      
                      const response = await propertiesAPI.getAll(params);
                      return response;
                    })(),
                    api.get('/properties/diamond?limit=10')
                  ]);
                  
                  // Update properties if successful
                  if (propertiesPromise.status === 'fulfilled') {
                    const response = propertiesPromise.value;
                    if (response.data.properties) {
                      setProperties(response.data.properties);
                      setCurrentPage(response.data.pagination.page);
                      setTotalPages(response.data.pagination.total_pages);
                      setTotalProperties(response.data.pagination.total);
                      setHasMorePages(response.data.pagination.has_next);
                    } else {
                      setProperties(response.data);
                    }
                  }
                  
                  // Update diamond properties if successful
                  if (diamondPromise.status === 'fulfilled') {
                    setDiamondProperties(diamondPromise.value.data || []);
                  }
                  
                  toast.success(txt.dataRefreshed || 'Dados atualizados!');
                } catch (error) {
                  console.error('Error refreshing data:', error);
                  toast.error(txt.errorLoadingProperties);
                } finally {
                  setLoading(false);
                  setDiamondLoading(false);
                }
              }}
              className="gap-2"
              data-testid="refresh-button"
              title={txt.refreshTooltip}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {txt.refresh}
            </Button>
          </div>
        </div>
      </div>

      {/* Search Bar - Hidden on mobile to save space */}
      <div className="hidden md:block bg-white border-b py-4 lg:sticky lg:top-16 z-40">
        <HeroSearch />
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-background">
        <div className="container mx-auto px-2 md:px-4 py-2 md:py-6">
          {/* Mobile View Controls */}
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" data-testid="mobile-filters-button">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  {txt.filters}
                  {getActiveFilterCount() > 0 && (
                    <Badge variant="secondary" className="ml-2">{getActiveFilterCount()}</Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[400px] overflow-y-auto flex flex-col" aria-describedby={undefined}>
                <SheetHeader>
                  <SheetTitle>{txt.filters}</SheetTitle>
                  {/* R3-2: Add SheetDescription for accessibility, but hide it visually */}
                  <SheetDescription className="sr-only">
                    {txt.filtersDescription || 'Filter properties by various criteria'}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 flex-1 overflow-y-auto pb-4">
                  <FilterPanel />
                </div>
                  
                {/* Apply Filters Button - Fixed at bottom of drawer */}
                <div className="sticky bottom-0 p-4 bg-white dark:bg-gray-900 border-t -mx-6 px-6">
                  <Button 
                    onClick={() => {
                      setMobileFiltersOpen(false);
                      setTimeout(() => {
                        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 300);
                    }}
                    className="w-full bg-[#00C2D1] hover:bg-[#00A8B5]"
                    data-testid="apply-filters-button"
                  >
                    {txt.applyFilters}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveSearch}
                data-testid="mobile-save-search-button"
              >
                <Bookmark className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMap(!showMap)}
                data-testid="mobile-map-toggle"
              >
                {showMap ? (
                  <>
                    <Grid3x3 className="w-4 h-4 mr-2" />
                    {txt.list}
                  </>
                ) : (
                  <>
                    <MapIcon className="w-4 h-4 mr-2" />
                    {txt.map}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Quick Search with Filters - Mobile */}
          <div className="mb-3 md:hidden">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl border-2 border-cyan-200 shadow-md p-3 space-y-2.5">
              {/* Header */}
              <div className="flex items-center gap-2 pb-2 border-b border-cyan-200">
                <Search className="w-5 h-5 text-cyan-600" />
                <h3 className="text-sm font-semibold text-gray-800">{language === 'pt' ? 'Encontre seu imóvel ideal' : 'Find your ideal property'}</h3>
              </div>

              {/* Location - Destaque */}
              <div className="bg-white rounded-lg p-2 shadow-sm">
                <Label className="text-xs font-medium text-gray-700 mb-1 block flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-cyan-600" />
                  {txt.whereToStay}
                </Label>
                <LocationAutocomplete
                  value={searchLocation}
                  onChange={setSearchLocation}
                  placeholder={language === 'pt' ? 'Ex: Orlando, Kissimmee...' : 'Ex: Orlando, Kissimmee...'}
                />
              </div>
              
              {/* Guests and Property Type - Com ícones */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-lg p-2 shadow-sm">
                  <Label className="text-xs font-medium text-gray-700 mb-1 block flex items-center gap-1">
                    <Users className="w-3 h-3 text-cyan-600" />
                    {txt.guests}
                  </Label>
                  <Select value={guests} onValueChange={setGuests}>
                    <SelectTrigger className="text-xs h-8 border-gray-300">
                      <SelectValue placeholder={language === 'pt' ? 'Quantos?' : 'How many?'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{txt.any}</SelectItem>
                      <SelectItem value="1">{language === 'pt' ? '1 pessoa' : '1 person'}</SelectItem>
                      <SelectItem value="2">{language === 'pt' ? '2 pessoas' : '2 people'}</SelectItem>
                      <SelectItem value="4">{language === 'pt' ? '4 pessoas' : '4 people'}</SelectItem>
                      <SelectItem value="6">{language === 'pt' ? '6 pessoas' : '6 people'}</SelectItem>
                      <SelectItem value="8">{language === 'pt' ? '8+ pessoas' : '8+ people'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-white rounded-lg p-2 shadow-sm">
                  <Label className="text-xs font-medium text-gray-700 mb-1 block flex items-center gap-1">
                    <HomeIcon className="w-3 h-3 text-cyan-600" />
                    {txt.type}
                  </Label>
                  <Select value={propertyType} onValueChange={setPropertyType}>
                    <SelectTrigger className="text-xs h-8 border-gray-300">
                      <SelectValue placeholder={language === 'pt' ? 'Qual?' : 'Which?'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{txt.any}</SelectItem>
                      <SelectItem value="house">🏠 {txt.house}</SelectItem>
                      <SelectItem value="apartment">🏢 {txt.apartment}</SelectItem>
                      <SelectItem value="condo">🏘️ {txt.condo}</SelectItem>
                      <SelectItem value="villa">🏰 {txt.villa}</SelectItem>
                      <SelectItem value="townhouse">🏡 {txt.townhouse}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price Range - Input fields */}
              <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm">
                <Label className="text-xs font-medium text-gray-700 flex items-center gap-1 mb-2">
                  <DollarSign className="w-3 h-3 text-cyan-600" />
                  <span className="hidden xs:inline">{txt.pricePerNight}</span>
                  <span className="xs:hidden">{txt.minPrice}/{txt.maxPrice}</span>
                </Label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                    <Input
                      type="number"
                      min="0"
                      placeholder={txt.minPrice || "Mín"}
                      value={priceRange[0] || ''}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val) && val >= 0) {
                          setPriceRange([val, priceRange[1]]);
                        } else if (e.target.value === '') {
                          setPriceRange([0, priceRange[1]]);
                        }
                      }}
                      onBlur={(e) => {
                        const val = Math.max(0, parseInt(e.target.value) || 0);
                        setPriceRange([val, priceRange[1]]);
                      }}
                      className="pl-5 h-8 sm:h-7 text-xs w-full"
                      data-testid="filter-price-min-header"
                    />
                  </div>
                  <span className="text-gray-300 text-xs flex-shrink-0">-</span>
                  <div className="relative flex-1 min-w-0">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                    <Input
                      type="number"
                      min="0"
                      placeholder={txt.maxPrice || "Máx"}
                      value={priceRange[1] === 10000 ? '' : priceRange[1]}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val) && val >= 0) {
                          setPriceRange([priceRange[0], val]);
                        } else if (e.target.value === '') {
                          setPriceRange([priceRange[0], 10000]);
                        }
                      }}
                      onBlur={(e) => {
                        const val = Math.max(0, parseInt(e.target.value) || 10000);
                        setPriceRange([priceRange[0], val]);
                      }}
                      className="pl-5 h-8 sm:h-7 text-xs w-full"
                      data-testid="filter-price-max-header"
                    />
                  </div>
                </div>
              </div>

              {/* Search Button - Mais chamativo */}
              <Button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (searchLocation.trim()) params.set('location', searchLocation);
                  if (guests !== 'any') params.set('guests', guests);
                  window.location.href = `/search?${params.toString()}`;
                }}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all h-10 text-sm"
              >
                <Search className="w-4 h-4 mr-2" />
                {txt.searchNow}
              </Button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Filters Sidebar - Desktop */}
            <aside className="hidden lg:block lg:col-span-3 space-y-6">
              <div className="bg-white rounded-xl p-6 border shadow-sm sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <h3 className="font-semibold text-lg mb-4 font-['Montserrat'] text-gray-900">{txt.filters}</h3>
                <FilterPanel />
              </div>
            </aside>

            {/* Results / Map */}
            <main ref={resultsRef} className="lg:col-span-9">
              {/* Compare Mode Toggle */}
              <div className="mb-4 flex justify-end">
                <Button
                  variant={compareMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCompareMode(!compareMode)}
                  data-testid="compare-mode-toggle"
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  {compareMode ? txt.exitMode : txt.compareMode}
                </Button>
              </div>
              
              {/* Quick Access Button - Favorites */}
              {isLoggedIn && (
                <div className="mb-4">
                  <Button
                    onClick={() => navigate('/favorites')}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2 h-12 bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 border-red-200 text-red-700 font-medium"
                  >
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                    </svg>
                    <span>{txt.myFavorites}</span>
                    {favoritesCount > 0 && (
                      <span className="ml-auto bg-red-200 text-red-800 text-xs font-bold px-2 py-1 rounded-full">
                        {favoritesCount}
                      </span>
                    )}
                  </Button>
                </div>
              )}
              
              {/* Tabs for Search Methods */}
              <Tabs defaultValue="filters" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="filters" className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4" />
                    <span>{txt.searchByFilters}</span>
                  </TabsTrigger>
                  <TabsTrigger value="image" className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    <span>{txt.searchByImage}</span>
                  </TabsTrigger>
                </TabsList>

                {/* Tab Content: Filter-based Search */}
                <TabsContent value="filters" className="space-y-4">
              {/* Diamond Carousel - Only show when NO filters are active */}
              {!diamondLoading && diamondProperties.length > 0 && !hasActiveFilters() && (
                <motion.div 
                  className="mb-8 md:mb-12 relative overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Premium Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100 rounded-3xl" />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-200/50 via-transparent to-transparent rounded-3xl" />
                  
                  {/* Decorative sparkles */}
                  <motion.div 
                    className="absolute top-10 left-10 w-3 h-3 bg-amber-400 rounded-full"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <motion.div 
                    className="absolute top-20 right-20 w-2 h-2 bg-yellow-400 rounded-full"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  />
                  <motion.div 
                    className="absolute bottom-20 left-1/4 w-4 h-4 bg-amber-300 rounded-full"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.8, 0.3] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  />
                  
                  <div className="relative p-6 md:p-10 rounded-3xl border-2 border-amber-300/50 shadow-2xl">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                      <div className="flex items-center gap-4">
                        <motion.div 
                          className="bg-gradient-to-br from-amber-400 to-yellow-500 p-4 rounded-2xl shadow-lg glow-gold"
                          animate={{ rotate: [0, 5, -5, 0] }}
                          transition={{ duration: 4, repeat: Infinity }}
                        >
                          <Sparkles className="w-8 h-8 text-amber-900" />
                        </motion.div>
                        <div>
                          <h2 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 bg-clip-text text-transparent">
                            ✨ {txt.diamondProperties}
                          </h2>
                          <p className="text-amber-700 text-sm md:text-base mt-1">
                            {diamondProperties.length} {diamondProperties.length === 1 
                              ? (language === 'pt' ? 'propriedade exclusiva' : 'exclusive property') 
                              : (language === 'pt' ? 'propriedades exclusivas' : 'exclusive properties')}
                          </p>
                        </div>
                      </div>
                      
                      <motion.div 
                        className="bg-gradient-to-r from-amber-500 to-yellow-400 px-6 py-3 rounded-full shadow-lg"
                        whileHover={{ scale: 1.05 }}
                      >
                        <p className="text-amber-900 font-bold text-sm md:text-base flex items-center gap-2">
                          <Crown className="w-5 h-5" />
                          {txt.premiumSelection}
                        </p>
                      </motion.div>
                    </div>

                    {/* Carousel */}
                    <div className="overflow-hidden -mx-6 px-6 md:mx-0 md:px-0">
                      <Carousel
                        opts={{
                          align: "start",
                          loop: diamondProperties.length > 2,
                          skipSnaps: false,
                        }}
                        setApi={setCarouselApi}
                        className="w-full"
                      >
                        <CarouselContent className="ml-0 md:-ml-6">
                          {diamondProperties.map((property, index) => (
                            <CarouselItem 
                              key={property.id} 
                              className={`pl-0 md:pl-6 basis-full sm:basis-[90%] md:basis-1/2 xl:basis-1/3 ${index > 0 ? 'pl-4 sm:pl-4' : ''}`}
                            >
                              <DiamondPropertyCard property={property} txt={txt} />
                            </CarouselItem>
                          ))}
                        </CarouselContent>
                        <div className="flex justify-center gap-4 mt-6">
                          <CarouselPrevious className="static translate-y-0 bg-white border-amber-300 hover:bg-amber-50" />
                          <CarouselNext className="static translate-y-0 bg-white border-amber-300 hover:bg-amber-50" />
                        </div>
                      </Carousel>
                    </div>

                    {/* Footer Text */}
                    <p className="text-center text-amber-700 mt-8 text-sm md:text-base flex items-center justify-center gap-2">
                      {txt.diamondPremiumText}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Header */}
              <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-muted-foreground" data-testid="results-count">
                  {loading ? txt.loading : (
                    properties.length === 0 
                      ? txt.noPropertiesFound 
                      : properties.length === 1 
                        ? `1 ${txt.propertyFound}`
                        : `${properties.length} ${txt.propertiesFound}`
                  )}
                </p>
                
                <div className="hidden lg:flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveSearch}
                    data-testid="desktop-save-search-button"
                  >
                    <Bookmark className="w-4 h-4 mr-2" />
                    {txt.saveSearch}
                  </Button>

                  <Button
                    variant={showMap ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowMap(!showMap)}
                    data-testid="desktop-map-toggle"
                  >
                    <MapIcon className="w-4 h-4 mr-2" />
                    {showMap ? txt.hideMap : txt.showMap}
                  </Button>

                  {/* Grid/List Toggle */}
                  {!showMap && (
                    <div className="flex rounded-lg border overflow-hidden">
                      <Button
                        variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('grid')}
                        className="rounded-none border-0"
                        data-testid="view-mode-grid"
                      >
                        <Grid3x3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('list')}
                        className="rounded-none border-0"
                        data-testid="view-mode-list"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {showMap ? (
                /* Map View */
                <div className="h-[400px] md:h-[500px] lg:h-[calc(100vh-12rem)] rounded-xl overflow-hidden border shadow-sm">
                  <MapView
                    properties={(() => {
                      // Combine and deduplicate properties by ID
                      const combined = [...(!hasActiveFilters() ? diamondProperties : []), ...properties];
                      const seen = new Set();
                      return combined.filter(p => {
                        if (seen.has(p.id)) return false;
                        seen.add(p.id);
                        return true;
                      });
                    })()}
                    onPolygonChange={setPolygon}
                    enableDrawing={true}
                  />
                </div>
              ) : (
                /* Grid/List View */
                <>
                  {/* Grid View */}
                  {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {loading
                        ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
                        : properties.map((property) => (
                            <PropertyCard 
                              key={property.id} 
                              property={property} 
                              showCompare={compareMode}
                              onCompareToggle={handleCompareUpdate}
                            />
                          ))}
                    </div>
                  )}

                  {/* List View */}
                  {viewMode === 'list' && (
                    <div className="space-y-4">
                      {loading
                        ? Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
                              <div className="flex gap-4">
                                <div className="w-48 h-32 bg-gray-200 rounded-lg flex-shrink-0" />
                                <div className="flex-1 space-y-3">
                                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                                  <div className="flex gap-4">
                                    <div className="h-4 bg-gray-200 rounded w-16" />
                                    <div className="h-4 bg-gray-200 rounded w-16" />
                                    <div className="h-4 bg-gray-200 rounded w-16" />
                                  </div>
                                </div>
                                <div className="w-24 space-y-2">
                                  <div className="h-8 bg-gray-200 rounded" />
                                  <div className="h-10 bg-gray-200 rounded" />
                                </div>
                              </div>
                            </div>
                          ))
                        : properties.map((property) => (
                            <motion.div
                              key={property.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-all p-4 cursor-pointer"
                              onClick={() => navigate(`/property/${property.id}`)}
                            >
                              <div className="flex gap-4">
                                {/* Image */}
                                <div className="w-48 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                  {property.images?.[0]?.image_url ? (
                                    <img 
                                      src={property.images[0].image_url} 
                                      alt={property.title}
                                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <HomeIcon className="w-8 h-8 text-gray-300" />
                                    </div>
                                  )}
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div>
                                      <h3 className="font-semibold text-lg text-gray-900 line-clamp-1">{property.title}</h3>
                                      <p className="text-sm text-gray-500 flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5" />
                                        {property.city}, {property.state}
                                      </p>
                                    </div>
                                    {property.plan_type === 'diamond' && (
                                      <Badge className="bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900">
                                        <Sparkles className="w-3 h-3 mr-1" />
                                        Diamond
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                                    <span className="flex items-center gap-1">
                                      <Users className="w-4 h-4" /> {property.max_guests} {txt.guests}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Bed className="w-4 h-4" /> {property.bedrooms} {txt.bedrooms}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Bath className="w-4 h-4" /> {property.bathrooms} {txt.bathrooms}
                                    </span>
                                  </div>

                                  {/* Amenities preview */}
                                  <div className="flex flex-wrap gap-1.5">
                                    {property.amenities?.slice(0, 4).map((amenity, idx) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">
                                        {amenity}
                                      </Badge>
                                    ))}
                                    {property.amenities?.length > 4 && (
                                      <Badge variant="outline" className="text-xs">
                                        +{property.amenities.length - 4}
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                {/* Price & Actions */}
                                <div className="flex flex-col items-end justify-between flex-shrink-0">
                                  <div className="text-right">
                                    <span className="text-2xl font-bold text-gray-900">${property.price_per_night || property.price || property.nightly_price_suggestion || 0}</span>
                                    <span className="text-sm text-gray-500">{txt.perNight}</span>
                                  </div>
                                  <Button 
                                    size="sm"
                                    className="bg-[#00C2D1] hover:bg-[#00A8B5]"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/property/${property.id}`);
                                    }}
                                  >
                                    {txt.view}
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                    </div>
                  )}

                  {/* Load More Button */}
                  {!loading && hasMorePages && (
                    <div className="flex flex-col items-center gap-3 py-8">
                      <p className="text-sm text-gray-600">
                        {txt.showingOf} {properties.length} {txt.of} {totalProperties} {totalProperties === 1 ? txt.propertyLabel : txt.propertiesLabel}
                      </p>
                      <Button
                        onClick={loadMoreProperties}
                        disabled={loadingMore}
                        size="lg"
                        variant="outline"
                        className="min-w-[200px]"
                      >
                        {loadingMore ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2" />
                            {txt.loading}
                          </>
                        ) : (
                          <>
                            {txt.loadMore}
                            <span className="ml-2 text-xs text-gray-500">
                              ({txt.page} {currentPage}/{totalPages})
                            </span>
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {!loading && properties.length === 0 && (
                    <div className="text-center py-20" data-testid="no-results">
                      <p className="text-lg text-muted-foreground mb-4">
                        {txt.noResults}
                      </p>
                      <Button
                        variant="outline"
                        onClick={clearAllFilters}
                      >
                        {txt.clearFilters}
                      </Button>
                    </div>
                  )}
                </>
              )}
                </TabsContent>

                {/* Tab Content: Image Search */}
                <TabsContent value="image" className="space-y-4">
                  <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200">
                    <CardContent className="p-6 space-y-4">
                      {/* Header */}
                      <div className="text-center space-y-3">
                        <div className="flex items-center justify-center gap-2">
                          <ImageIcon className="w-8 h-8 text-cyan-600" />
                          <h2 className="text-2xl font-bold text-gray-800">{txt.searchByImage}</h2>
                        </div>
                        <p className="text-base font-medium text-cyan-700">
                          {txt.pastePhotoHere}
                        </p>
                        
                        {/* Step by step instructions */}
                        <div className="bg-white/70 rounded-lg p-4 text-left max-w-md mx-auto">
                          <p className="text-sm text-gray-700 mb-2">{txt.imageSearchStep1}</p>
                          <p className="text-sm text-gray-700 mb-2">{txt.imageSearchStep2}</p>
                          <p className="text-sm text-gray-700">{txt.imageSearchStep3}</p>
                        </div>
                        
                        <p className="text-xs text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-full inline-block">
                          {txt.imageSearchTip}
                        </p>
                      </div>

                      {/* Upload Area */}
                      <div 
                        className="border-2 border-dashed border-cyan-400 rounded-lg p-8 bg-white hover:bg-cyan-50 transition-colors cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                        onPaste={handleImagePaste}
                        tabIndex={0}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                        
                        {!imagePreview ? (
                          <div className="text-center space-y-4">
                            <Upload className="w-16 h-16 text-cyan-500 mx-auto" />
                            <div>
                              <p className="text-lg font-semibold text-gray-700">
                                {language === 'pt' ? 'Clique para fazer upload ou cole uma imagem' : 'Click to upload or paste an image'}
                              </p>
                              <p className="text-sm text-gray-500 mt-2">
                                {language === 'pt' ? 'Ctrl+V para colar | Arraste e solte | Clique para selecionar' : 'Ctrl+V to paste | Drag and drop | Click to select'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="relative max-w-md mx-auto">
                              <img 
                                src={imagePreview} 
                                alt="Preview" 
                                loading="lazy"
                                decoding="async"
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearImageSearch();
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-center text-sm text-gray-600">
                              Imagem carregada com sucesso!
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Search Button */}
                      <Button
                        onClick={searchByImage}
                        disabled={!selectedImage || imageSearchLoading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-6 text-lg font-semibold"
                      >
                        {imageSearchLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                            {txt.searching}
                          </>
                        ) : (
                          <>
                            <Search className="w-5 h-5 mr-2" />
                            {txt.searchProperty}
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Results */}
                  {imageSearchResults.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-xl font-bold text-gray-800">
                        {txt.imageSearchResults} ({imageSearchResults.length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {imageSearchResults.map((property) => (
                          <div key={property.id} className="relative">
                            <PropertyCard property={property} />
                            {/* Similarity Badge */}
                            <div className="absolute top-2 left-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg z-10">
                              {property.match_similarity}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results - Bug 21 fix: Only show after search was attempted */}
                  {!imageSearchLoading && imageSearchResults.length === 0 && hasSearchedImage && (
                    <Card className="bg-gray-50">
                      <CardContent className="p-8 text-center space-y-2">
                        <p className="text-lg font-semibold text-gray-700">
                          {txt.noSimilarFound}
                        </p>
                        <p className="text-sm text-gray-500">
                          {txt.tryAnotherPhoto}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </main>
          </div>
        </div>
      </div>

      {/* Floating Compare Button - Bug 20 fix: Position above mobile nav bar */}
      {compareCount > 0 && (
        <div className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-[9999]">
          <Button
            size="lg"
            className="bg-primary hover:bg-primary/90 shadow-2xl text-white rounded-full px-4 lg:px-6 py-4 lg:py-6"
            onClick={() => navigate('/compare')}
            data-testid="compare-floating-button"
          >
            <GitCompare className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">{txt.compare}</span> {compareCount} <span className="hidden sm:inline">{compareCount === 1 ? txt.property : txt.properties}</span>
            <span className="ml-2 bg-white text-primary rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
              {compareCount}
            </span>
          </Button>
        </div>
      )}

      <Footer />

      {/* Back to Top Button */}
      <BackToTopButton />

      {/* Save Search Modal */}
      <SaveSearchModal
        open={saveModalOpen}
        onClose={(success) => {
          setSaveModalOpen(false);
          if (success) {
            toast.success(language === 'pt' ? 'Você pode gerenciar suas buscas salvas na página Buscas Salvas' : 'You can manage your saved searches on the Saved Searches page');
          }
        }}
        filters={getCurrentFilters()}
        polygon={polygon}
      />
    </div>
  );
};

export default SearchResults;
